import { useTranslations } from 'next-intl'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import * as RPNInput from 'react-phone-number-input'
import { type MoneyType, type PriceType, type TranslatableType } from '@/types/general'
import { I18nMessages, MetaData, RouteParams } from '@/types/meta'
import { CategoryType } from '@/types/category'
import { ProductType } from '@/types/product'
import config from '@/config'

// Date utilities from date-utils module
export { formatDateTime, formatDate } from './date-utils'

/**
 * Utility function to merge class names conditionally.
 *
 * @param inputs ClassValue[]
 * @returns {string} Merged class names.
 */
export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs))

/**
 * Returns an array of available locales in the format "code-countryCode".
 *
 * @param metaData
 * @returns {string[]} An array of available locales.
 */
export const getAvailableLocales = (metaData: MetaData): string[] =>
  metaData.locales.map(l => `${l.code}-${l.countryCode}`)

/**
 * Returns the requested locale if it is available, otherwise returns the default locale.
 *
 * @param metaData
 * @param locale
 * @returns {string} The requested locale or the default locale.
 */
export const getRequestedLocale = (metaData: MetaData, locale: string): string => {
  return getAvailableLocales(metaData).includes(locale) ? locale : metaData.generalSettings.appDefaultLocale
}

/**
 * Formats messages for use with next-intl.
 *
 * @param messages Record<string, string>
 * @param locale string
 * @param fallbackMessages
 * @param defaultLocale
 * @returns {Record<string, string>} Formatted messages for the specified locale.
 */
export const formatMessagesForNextIntl = (
  messages: I18nMessages,
  locale: string,
  fallbackMessages: Record<string, string> = {},
  defaultLocale?: string
): Record<string, string> => {
  const formatted: Record<string, string> = fallbackMessages
  for (const [key, translations] of Object.entries(messages)) {
    // Try current locale, then defaultLocale, then fall back to en-US
    formatted[key] =
      translations[locale] ||
      (defaultLocale && translations[defaultLocale]) ||
      translations['en-US'] ||
      Object.values(translations)[0] // Final fallback to the first available translation
  }

  return formatted
}

/**
 * A utility function that retrieves a translated value for a given key if it exists in the translation object,
 * otherwise returns the key itself as a fallback.
 *
 * @param {ReturnType<typeof useTranslations>} t - The translation object, typically returned from the `useTranslations` hook,
 * containing methods for handling translations.
 * @param {string} key - The identifier string used to look up the translated value from the translation object.
 * @param {Record<string, any>} values - Optional values object for translation interpolation (e.g., {count: 5}).
 * @returns {string} The translated value corresponding to the provided key if it exists, or the key itself if no translation is found.
 */
export const tOptional = (t: ReturnType<typeof useTranslations>, key: string, values?: Record<string, any>): string =>
  t.has(key) ? t(key, values) : key

/**
 * Extract error property from API error response
 * @param error any - The error object from API response
 * @param defaultMessage string - Default property to return if no error property is found
 * @returns string - The extracted error property
 */
export const getErrorMessage = (error: any, defaultMessage: string = 'An error occurred'): string => {
  if (!error) {
    return defaultMessage
  }

  if (error.response) {
    const { data } = error.response

    if (data?.message) {
      return data.message
    }

    if (data?.error?.message) {
      return data.error.message
    }

    if (data?.error && typeof data.error === 'string') {
      return data.error
    }
  }

  if (error.message) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return defaultMessage
}

/**
 * Set validation errors on a form using react-hook-form.
 *
 * @param error any - The error object from the backend
 * @param setError any - The form.setError function from react-hook-form
 */
export const setValidationErrors = (error: any, setError: any) => {
  const errors = error?.response?.data?.items || {}
  Object.entries(errors).forEach(([key, message]) => {
    // Convert backend field names to react-hook-form field paths
    // e.g., "oemNumbers[0]" -> "oemNumbers.0"
    // e.g., "translations[en-US][title]" -> "translations.en-US.title"
    const fieldPath = key.replace(/\[(\d+)]/g, '.$1').replace(/\[([^\]]+)]/g, '.$1')
    setError(fieldPath, {
      type: 'manual',
      message: message as string
    })
  })
}

/**
 * Verifies a JWT by calling the backend. If the token is invalid/expired and a
 * refreshToken is provided, attempts to refresh and returns new tokens.
 * Returns null if authentication cannot be established.
 */
export const isVerifiedJwt = async (
  accessToken: string | undefined,
  refreshToken?: string | undefined
): Promise<{ newTokens: { accessToken: string; refreshToken: string } | null } | null> => {
  // Verify the access token locally by checking the exp claim — no network call needed.
  // Only reach out to the backend when the access token is actually expired.
  if (accessToken) {
    try {
      const parts = accessToken.split('.')
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]))
        if (typeof payload.exp === 'number' && payload.exp * 1000 > Date.now()) {
          // Token is valid and not expired
          return { newTokens: null }
        }
      }
    } catch {
      // malformed token — fall through to refresh
    }
  }

  // accessToken missing, expired, or malformed — try refresh
  if (!refreshToken) {
    return null
  }

  try {
    const response = await fetch(`${config.API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    })
    if (!response.ok) {
      return null
    }
    const tokens = (await response.json()) as { accessToken: string; refreshToken: string }
    return { newTokens: tokens }
  } catch {
    return null
  }
}

/**
 * Limit text
 * @param text string
 * @param limit number
 */
export const textLimit = (text: string, limit: number = 100) => {
  if (!text) {
    return ''
  }

  return text.length > limit ? text.slice(0, limit) + '...' : text
}

/**
 * Creates a URLSearchParams object from a given parameters object.
 *
 * @param params Record<string, any> - An object containing key-value pairs to be converted into URL search parameters.
 * @returns {URLSearchParams} A URLSearchParams object containing the provided parameters.
 */
export const createSearchParams = (params: Record<string, any>): URLSearchParams => {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          searchParams.append(key, value.join(','))
        }
      } else {
        searchParams.append(key, value.toString())
      }
    }
  })

  return searchParams
}

/**
 * Get country code from locale
 * Extract country code from locale (e.g., 'en-US' -> 'US', 'tr-TR' -> 'TR')
 *
 * @param locale string - Locale in the format 'en-US', 'tr-TR', etc.
 * @returns string - Country code in uppercase (e.g., 'US', 'TR')
 */
export const getCountryCodeFromLocale = (locale: string): string => {
  const parts = locale.split('-')
  return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
}

/**
 * Detect user's country code from browser locale settings
 *
 * @returns string | undefined - Detected country code in uppercase (e.g., 'US', 'TR') or undefined if detection fails
 */
export const detectCountryFromLocale = (): RPNInput.Country | undefined => {
  try {
    if (typeof navigator === 'undefined') {
      return undefined
    }
    const region = new Intl.Locale(navigator.language).region
    return region as RPNInput.Country
  } catch {
    return undefined
  }
}

/**
 * Check if a translatable object has a translation for the given locale
 * @param data TranslatableType - The translatable object containing translations
 * @param localeCode string - The locale code to check (e.g., 'en-US', 'tr-TR')
 * @returns boolean - True if the translation exists, false otherwise
 */
export const hasTranslation = (data: TranslatableType, localeCode: string): boolean => {
  return data.translations?.[localeCode] !== undefined
}

/**
 * Get localized value from a translatable object
 * @param data ITranslatable - The translatable object containing translations
 * @param field string - The field to retrieve from translations (e.g., 'name', 'description')
 * @param locale string - The locale to use for translation (e.g., 'en-US', 'tr-TR')
 * @param defaultLocale string - The default locale to fall back to if the requested locale is not available (default: 'en-US')
 */
export const getLocalizedValue = (
  data: TranslatableType,
  field: string,
  locale: string,
  defaultLocale: string = 'en-US'
) => {
  if (!data || !data.translations) {
    return '-'
  }

  // Try to get the current locale's translation
  if (data.translations[locale]) {
    return data.translations[locale][field as keyof (typeof data.translations)[typeof locale]] || '-'
  }

  // Try to get the defaultLocale's translation
  if (defaultLocale && data.translations[defaultLocale]) {
    return data.translations[defaultLocale][field as keyof (typeof data.translations)[typeof defaultLocale]] || '-'
  }

  // Fallback to the first available translation
  const firstLocale = Object.keys(data.translations)[0]
  return firstLocale
    ? data.translations[firstLocale][field as keyof (typeof data.translations)[typeof firstLocale]] || '-'
    : '-'
}

export const getCategoryPath = (category: CategoryType, locale: string, defaultLocale?: string) => {
  if (!category.parents || category.parents.length === 0) {
    return '-'
  }

  // Get all parent names in the correct order
  const parentNames = category.parents.map(parent => {
    if (parent.translations[locale]) {
      return parent.translations[locale].name
    }

    // Try to get the defaultLocale's translation
    if (defaultLocale && parent.translations[defaultLocale]) {
      return parent.translations[defaultLocale].name
    }

    // Fallback to the first available translation
    const firstLocale = Object.keys(parent.translations)[0]
    return parent.translations[firstLocale]?.name || 'Unnamed Category'
  })

  return parentNames.join(' > ')
}

/**
 * Get picture URL
 *
 * @param path string
 * @returns string
 */
export const getPictureUrl = (path: string): string => `/api/shared/media/picture/show/${path}`

/**
 * Get video streaming URL. Uses the dedicated video endpoint which supports HTTP range
 * requests, so playback can start and seek without downloading the whole file first.
 *
 * @param path string
 * @returns string
 */
export const getVideoUrl = (path: string): string => `/api/shared/media/video/show/${path}`

/**
 * Get file inline-preview URL. Uses the file "show" endpoint which serves the file with
 * Content-Disposition: inline, so browser-viewable files (e.g. PDF, images) are displayed
 * instead of downloaded. Access-level checks still apply (auth is injected by the /api proxy).
 *
 * @param path string
 * @returns string
 */
export const getFileShowUrl = (path: string): string => `/api/shared/media/file/show/${path}`

/**
 * Get resized picture URL
 *
 * @param path string
 * @param width number
 * @param height number
 * @returns string
 */
export const getResizedPictureUrl = (path: string, width: number, height: number): string =>
  `/api/shared/media/picture/${width}/${height}/${path}`

/**
 * Converts file size in bytes to the most appropriate unit (KB, MB, GB, TB) with formatted output
 *
 * @param bytes number - The file size in bytes
 * @param decimals number - Number of decimal places to include in the formatted output (default: 2)
 * @returns string - The file size with the appropriate unit suffix
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) {
    return '0 Bytes'
  }

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  // Prevent array index out of bounds
  const sizeIndex = Math.min(i, sizes.length - 1)
  const value = bytes / Math.pow(k, sizeIndex)

  return value.toFixed(decimals) + ' ' + sizes[sizeIndex]
}

/**
 * Formats a number or MoneyType object as a localized currency string.
 *
 * @param value - A numeric value (defaults to USD) or a MoneyType object
 * @param locale - The locale to use for formatting (default: 'en-US')
 * @returns string - The formatted currency string
 */
export const formatMoney = (value: number | MoneyType | undefined | null, locale: string = 'en-US'): string => {
  if (value === undefined || value === null) {
    return '-'
  }

  const amount = typeof value === 'object' ? (value.amount ?? 0) : value
  const currency = typeof value === 'object' ? value.currencyCode : 'USD'
  const symbol = typeof value === 'object' ? value.currencySymbol : undefined

  if (!currency) {
    const formatted = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      amount
    )
    return symbol ? `${formatted} ${symbol}` : formatted
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * Formats the gross (tax-inclusive) amount of a PriceType as a localized currency string.
 */
export const formatPriceGross = (price: PriceType | null | undefined, locale: string = 'en-US'): string => {
  if (!price) {
    return '-'
  }
  return formatMoney(
    { amount: price.gross, currencyCode: price.currencyCode, currencySymbol: price.currencySymbol },
    locale
  )
}

/**
 * Formats the net (tax-exclusive) amount of a PriceType as a localized currency string.
 */
export const formatPriceNet = (price: PriceType | null | undefined, locale: string = 'en-US'): string => {
  if (!price) {
    return '-'
  }
  return formatMoney(
    { amount: price.net, currencyCode: price.currencyCode, currencySymbol: price.currencySymbol },
    locale
  )
}

/**
 * Formats a number according to the specified locale
 *
 * @param value - The numeric value to format
 * @param locale - The locale to use for formatting (default: 'en-US')
 * @returns string - The formatted number string
 */
export const formatNumber = (value: number | undefined | null, locale: string = 'en-US'): string => {
  if (value === undefined || value === null) {
    return '-'
  }

  return new Intl.NumberFormat(locale).format(value)
}

/**
 * Get file extension from file name
 *
 * @param filename
 * @return string - File extension in lowercase (e.g., 'jpg', 'png')
 */
export const getFileExtension = (filename: string): string => {
  const parts = filename.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

/**
 * Get translated content URL
 *
 * @param routeParams
 * @param module
 * @param locale
 * @param content
 */
export const getTranslatedContentUrl = (
  routeParams: RouteParams | undefined | null,
  module: string,
  locale: string,
  content: TranslatableType | CategoryType | ProductType
): string | null => {
  if (!routeParams || !content.translations) {
    return null
  }

  const translatedBasePaths = []
  if (module === 'photo-galleries') {
    translatedBasePaths.push(routeParams['corporate']?.[locale])
  }

  translatedBasePaths.push(routeParams[module]?.[locale] || module)

  const translatedContent = content.translations?.[locale]
  if (!translatedContent?.slug) {
    return null
  }

  const slugParts = []
  let base = null
  if (module === 'products' && 'parents' in content && content.parents) {
    base = content.parents
  } else if (module === 'product' && 'category' in content && content.category?.parents) {
    base = content.category.parents
  }

  if (base) {
    // Include parent category slugs in the URL
    const parentSlugs = base
      .map((parent: TranslatableType) => parent.translations?.[locale]?.slug)
      .filter((slug: string | undefined) => slug !== undefined)
    slugParts.push(...parentSlugs)
  }

  slugParts.push(translatedContent.slug)

  return `${config.APP_BASE_URL}/${locale}/${translatedBasePaths.join('/')}/${slugParts.join('/')}`.toLowerCase()
}
